/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {QFieldMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QFieldMetaData";
import {QFieldType} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QFieldType";


/*******************************************************************************
 ** Tracks a form's date-time fields whose typed date doesn't exist (e.g. Feb
 ** 31).  The browser gives such an input an empty value, the same as a cleared
 ** one - only validity.badInput tells the two apart - so a form's host would
 ** save it as "no date".  A bad field is reported as INVALID_DATE_TIME_VALUE
 ** instead, which the backend rejects, until the user really fixes or clears it.
 **
 ** The host can hand the marker back (e.g. when it redraws the form after a
 ** refused save).  Such a field shows empty with badInput off, so it is only
 ** released by a real edit: a date typed in, or bad input seen and then gone -
 ** never by just moving focus into or out of the untouched field.
 *******************************************************************************/
export default class DateTimeBadInputTracker
{
   public static readonly INVALID_DATE_TIME_VALUE = "Invalid date";

   /////////////////////////////////////////////////////////////////////////////
   // fields currently reported as INVALID_DATE_TIME_VALUE, and those of them //
   // whose input the browser has actually flagged as bad input               //
   /////////////////////////////////////////////////////////////////////////////
   private readonly badFieldNames = new Set<string>();
   private readonly sawBadInputFieldNames = new Set<string>();


   /*******************************************************************************
    ** Start with the form's DATE_TIME fields whose value is already the marker.
    *******************************************************************************/
   constructor(fields: QFieldMetaData[], values: Map<string, any>)
   {
      for (let field of fields ?? [])
      {
         if (field.type === QFieldType.DATE_TIME && values?.get(field.name) === DateTimeBadInputTracker.INVALID_DATE_TIME_VALUE)
         {
            this.badFieldNames.add(field.name);
         }
      }
   }


   /*******************************************************************************
    ** Is this field's date currently bad (so its empty form value must not be
    ** reported to the host)?
    *******************************************************************************/
   public isBad(fieldName: string): boolean
   {
      return (this.badFieldNames.has(fieldName));
   }


   /*******************************************************************************
    ** The names of the fields whose date is currently bad.
    *******************************************************************************/
   public getBadFieldNames(): string[]
   {
      return ([...this.badFieldNames]);
   }


   /*******************************************************************************
    ** For an input, keyup or blur event, return what the form should report to
    ** its host when the target's bad state changes: INVALID_DATE_TIME_VALUE when
    ** its date goes bad, or the input's own value once it is fixed or cleared.
    ** Null when nothing changed, or the target is not a named date-time input.
    *******************************************************************************/
   public onInputEvent(target: EventTarget): { fieldName: string, value: string } | null
   {
      if (!(target instanceof HTMLInputElement) || target.type !== "datetime-local" || !target.name)
      {
         return (null);
      }

      const fieldName = target.name;
      if (target.validity?.badInput === true)
      {
         this.sawBadInputFieldNames.add(fieldName);
         if (this.badFieldNames.has(fieldName))
         {
            return (null);
         }

         this.badFieldNames.add(fieldName);
         return ({fieldName, value: DateTimeBadInputTracker.INVALID_DATE_TIME_VALUE});
      }

      ///////////////////////////////////////////////////////////////////////////////////
      // not bad input now.  release a bad field only on a real edit: a date typed in, //
      // or bad input seen and then gone.  an untouched, handed-back field stays bad.  //
      ///////////////////////////////////////////////////////////////////////////////////
      if (!this.badFieldNames.has(fieldName) || (target.value === "" && !this.sawBadInputFieldNames.has(fieldName)))
      {
         return (null);
      }

      this.badFieldNames.delete(fieldName);
      this.sawBadInputFieldNames.delete(fieldName);
      return ({fieldName, value: target.value});
   }
}
